import React, { useState } from 'react';
import { playNote, stopNote } from '../utils/audioUtils';
import './Recorder.css';

interface NoteEvent {
  note: string;
  startTime: number;
  duration?: number;
}

interface RecorderProps {
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  recordedNotes: NoteEvent[];
}

const Recorder: React.FC<RecorderProps> = ({ 
  isRecording, 
  onStartRecording, 
  onStopRecording,
  recordedNotes
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  const playRecording = () => {
    if (recordedNotes.length === 0 || isPlaying) return;
    
    setIsPlaying(true);
    
    // Create a copy of the recorded notes
    const notes = [...recordedNotes];
    
    // Play each note at its recorded time
    notes.forEach((noteEvent) => {
      setTimeout(() => {
        playNote(noteEvent.note);
        
        // Stop the note after its recorded duration
        if (noteEvent.duration) {
          setTimeout(() => {
            stopNote(noteEvent.note);
          }, noteEvent.duration);
        }
      }, noteEvent.startTime);
    });
    
    // Calculate when playback will be complete
    const lastNote = notes[notes.length - 1];
    const playbackDuration = lastNote.startTime + (lastNote.duration || 500);
    
    // Reset playing state when done
    setTimeout(() => {
      setIsPlaying(false);
    }, playbackDuration + 100);
  };
  
  return (
    <div className="recorder-container">
      <div className="recorder-controls">
        <button 
          className={`record-button ${isRecording ? 'active' : ''}`}
          onClick={isRecording ? onStopRecording : onStartRecording}
          disabled={isPlaying}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
        
        <button 
          className={`play-button ${isPlaying ? 'active' : ''}`}
          onClick={playRecording}
          disabled={recordedNotes.length === 0 || isRecording || isPlaying}
        >
          Play Recording
        </button>
      </div>
      
      <div className="recorder-info">
        {isRecording && <div className="recording-indicator">Recording...</div>}
        {!isRecording && recordedNotes.length > 0 && (
          <div className="recording-stats">
            {recordedNotes.length} notes recorded ({(recordedNotes[recordedNotes.length - 1].startTime / 1000).toFixed(1)}s)
          </div>
        )}
      </div>
    </div>
  );
};

export default Recorder;
